import { useEffect, useState } from "react";
import { useSocket } from "../../context/SocketContext";

export interface RainEventMessage {
  type: "rain:started" | "rain:contribution" | "rain:closed" | "rain:awarded";
  timestamp: string;
  data: RainEventData;
}

export interface RainEventData {
  discord_id: string | null;
  twitch_id: string;
  twitch_name: string;
  amount?: number;
  totalPot?: number;
  participantCount?: number;
  awardedAmount?: number;
  timeRemaining?: number;
  maxPerUser?: number | null;
  topContributors?: {
    twitch_id: string;
    twitch_name: string;
    totalContributed: number;
  }[];
}

export interface RainContributor {
  twitch_id: string;
  twitch_name: string;
  amount: number;
  timestamp: string;
}

export interface RainOverlayState {
  isActive: boolean;
  broadcastedBy: string;
  broadcasterId: string;
  maxPerUser: number | null;
  totalPot: number;
  participantCount: number;
  timeRemaining: number;
  status: "idle" | "active" | "closed";
  contributors: RainContributor[];
  topContributors?: {
    twitch_id: string;
    twitch_name: string;
    totalContributed: number;
  }[];
  finalStats?: {
    totalPot: number;
    participantCount: number;
    awardPerPerson: number;
  };
}

const initialState: RainOverlayState = {
  isActive: false,
  broadcastedBy: "",
  broadcasterId: "",
  maxPerUser: null,
  totalPot: 0,
  participantCount: 0,
  timeRemaining: 60,
  status: "idle",
  contributors: [],
};

interface RainCurrentPayload {
  success: boolean;
  rainEvent: {
    twitch_name: string;
    twitch_id: string;
    maxPerUser: number | null;
    totalPot: number;
    participantCount: number;
    timeRemaining: number;
    contributors: { twitch_id: string; twitch_name: string; amount: number; createdAt: string }[];
  } | null;
}

export function useRainWebSocket() {
  const [state, setState] = useState<RainOverlayState>(initialState);
  const { socket } = useSocket();

  // Megosztott alkalmazó — ugyanazt a payload alakot (GET /rain/current
  // válasza) dolgozza fel, akár REST hívásból (első betöltés), akár a
  // socket "overlay:hydrate" eventből (minden connect/reconnect) érkezik.
  const applyRainPayload = (data: RainCurrentPayload) => {
    if (!data.success || !data.rainEvent) return;

    const r = data.rainEvent;
    setState((prev) => ({
      ...prev,
      isActive: true,
      broadcastedBy: r.twitch_name,
      broadcasterId: r.twitch_id,
      maxPerUser: r.maxPerUser ?? null,
      totalPot: r.totalPot ?? 0,
      participantCount: r.participantCount ?? 0,
      timeRemaining: r.timeRemaining ?? 0,
      status: "active",
      contributors: (r.contributors ?? []).map((c) => ({
        twitch_id: c.twitch_id,
        twitch_name: c.twitch_name,
        amount: c.amount,
        timestamp: c.createdAt,
      })),
    }));
  };

  useEffect(() => {
    if (!state.isActive || state.timeRemaining <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setState((prev) => ({
        ...prev,
        timeRemaining: Math.max(0, prev.timeRemaining - 1),
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, [state.isActive, state.timeRemaining]);

  // Hydration — ha az overlay (OBS böngésző forrás) frissül/újracsatlakozik
  // miközben épp fut egy rain, a socket eventek nélküle elvesznének, és az
  // overlay üresen maradna a következő contribution eseményig (akár 60mp-ig).
  // Ugyanaz a minta, mint a giveaway/contest/nyeremenyjatek/barkoba hydrationnél.
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      try {
        const res = await fetch("https://api.palinagyonbuzi.com/api/rain/current", {
          headers: { Authorization: "Bearer f2414060-927a-411b-a481-197241e5828d" },
        });
        const data: RainCurrentPayload = await res.json();
        if (cancelled) return;
        applyRainPayload(data);
      } catch (err) {
        console.error("[RAIN] Hydration error:", err);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, []);

  // Socket-alapú hydration — MINDEN connection/reconnect eseménynél lefut
  // (server.js io.on("connection", ...)), a fenti REST hívás pedig csak
  // az első mountnál. Enélkül egy OBS browser source frissítés / hálózati
  // megszakadás közben futó rain esemény elveszne az overlay-en.
  useEffect(() => {
    if (!socket) return;

    const handleHydrate = (data: { rain?: RainCurrentPayload }) => {
      if (data.rain) applyRainPayload(data.rain);
    };

    const s = socket as any;
    s.on("overlay:hydrate", handleHydrate);
    return () => {
      s.off("overlay:hydrate", handleHydrate);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleRainEventWrapper = (event: RainEventMessage) => {
      handleRainEvent(event);
    };

    const handleError = (error: Error | string) => {
      console.error("[RAIN] WebSocket error:", error);
    };

    const s = socket as any;
    s.on("rain:event", handleRainEventWrapper);
    s.on("error", handleError);
    s.emit("join-rain");
    console.log("[RAIN] Listening for rain events");

    return () => {
      s.off("rain:event", handleRainEventWrapper);
      s.off("error", handleError);
    };
  }, [socket]);

  const handleRainEvent = (event: RainEventMessage) => {
    console.log("[RAIN] Rain event:", event.type, event.data);

    switch (event.type) {
      case "rain:started":
        setState((prev) => ({
          ...prev,
          isActive: true,
          broadcastedBy: event.data.twitch_name,
          broadcasterId: event.data.twitch_id,
          maxPerUser: event.data.maxPerUser || null,
          totalPot: 0,
          participantCount: 0,
          timeRemaining: 60,
          status: "active",
          contributors: [],
        }));
        break;

      case "rain:contribution":
        setState((prev) => ({
          ...prev,
          totalPot: event.data.totalPot || 0,
          participantCount: event.data.participantCount || 0,
          timeRemaining: event.data.timeRemaining || 0,
          contributors: [
            {
              twitch_id: event.data.twitch_id,
              twitch_name: event.data.twitch_name,
              amount: event.data.amount || 0,
              timestamp: event.timestamp,
            },
            ...prev.contributors,
          ].slice(0, 20), // Limit to 20 contributors
        }));
        break;

      case "rain:closed":
        setState((prev) => ({
          ...prev,
          isActive: false,
          status: "closed",
          timeRemaining: 0,
          topContributors: event.data.topContributors || [],
          finalStats: {
            totalPot: event.data.totalPot || 0,
            participantCount: event.data.participantCount || 0,
            awardPerPerson: event.data.awardedAmount || 0,
          },
        }));
        break;

      case "rain:awarded":
        console.log(`[RAIN] ${event.data.twitch_name} received ${event.data.awardedAmount} ESPONT!`);
        break;
    }
  };

  return { state, socket };
}
